import React, { useState, useEffect, useCallback } from 'react';
import { Gem, Star, Lock, Package, ChevronDown, ChevronRight, Sparkles, Trophy, Sticker, Check, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../utils/api';


const RARITY_STYLES = {
  common:    { bg: 'bg-slate-500/10',   border: 'border-slate-400/20',  text: 'text-slate-400',   glow: '' },
  uncommon:  { bg: 'bg-green-500/10',   border: 'border-green-400/20',  text: 'text-green-400',   glow: '' },
  rare:      { bg: 'bg-blue-500/10',    border: 'border-blue-400/25',   text: 'text-blue-400',    glow: 'shadow-[0_0_8px_rgba(96,165,250,0.08)]' },
  epic:      { bg: 'bg-violet-500/10',  border: 'border-violet-400/25', text: 'text-violet-400',  glow: 'shadow-[0_0_12px_rgba(167,139,250,0.1)]' },
  legendary: { bg: 'bg-amber-500/10',   border: 'border-amber-400/30',  text: 'text-amber-400',   glow: 'shadow-[0_0_16px_rgba(251,191,36,0.12)]' },
  mythic:    { bg: 'bg-rose-500/10',    border: 'border-rose-400/30',   text: 'text-rose-400',    glow: 'shadow-[0_0_20px_rgba(251,113,133,0.15)]' },
};

const STICKER_ICONS = {
  grom: '🏄',
  dawn_patroller: '🌅',
  wave_whisperer: '🌊',
  barrel_dodger: '💨',
  tube_time: '🤙',
  swell_prophet: '🔮',
};

const TROPHY_ICONS = {
  salt_sage: '🧂',
  ocean_elder: '🐚',
  poseidons_crew: '🔱',
  shred_legend: '⚡',
  endless_summer: '☀️',
};

function MilestoneCard({ item, order, onOrder }) {
  const style = RARITY_STYLES[item.rarity] || RARITY_STYLES.common;
  const icon = item.type === 'sticker' ? STICKER_ICONS[item.id] : TROPHY_ICONS[item.id];
  const isOrdered = !!order;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`relative border rounded-2xl p-3.5 transition-all ${
        item.unlocked
          ? `${style.bg} ${style.border} ${style.glow}`
          : 'bg-white/[0.015] border-white/[0.04] opacity-50'
      }`}
      data-testid={`milestone-${item.id}`}
    >
      {/* Lock overlay */}
      {!item.unlocked && (
        <div className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/20 backdrop-blur-[1px] z-10">
          <div className="text-center">
            <Lock className="w-4 h-4 text-muted-foreground/30 mx-auto mb-1" />
            <p className="text-[9px] text-muted-foreground/30 font-medium">{item.aura_needed} more aura</p>
          </div>
        </div>
      )}

      {/* Icon + name */}
      <div className="flex items-start gap-2.5 mb-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${style.bg} border ${style.border}`}>
          {icon || (item.type === 'trophy' ? '🏆' : '✨')}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold ${item.unlocked ? style.text : 'text-muted-foreground/40'}`}>{item.name}</p>
          <p className="text-[9px] text-muted-foreground/50 leading-relaxed">{item.desc}</p>
        </div>
      </div>

      {/* Threshold + rarity */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className={`w-2.5 h-2.5 ${style.text} opacity-60`} />
          <span className="text-[9px] text-muted-foreground/40">{item.threshold} aura</span>
        </div>
        <span className={`text-[8px] font-bold uppercase tracking-wider ${style.text} opacity-70`}>{item.rarity}</span>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-2">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${item.progress}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className={`h-full rounded-full ${item.unlocked ? 'bg-current ' + style.text : 'bg-muted-foreground/20'}`}
        />
      </div>

      {/* Order button */}
      {item.unlocked && item.orderable && (
        <div className="mt-1">
          {isOrdered ? (
            <div className="flex items-center gap-1.5 text-[9px] text-green-400/60">
              <Check className="w-3 h-3" />
              <span>Ordered — {order.status}</span>
            </div>
          ) : (
            <div
              className={`w-full text-[9px] font-medium py-2 rounded-xl border transition-all inline-flex items-center justify-center gap-1.5 ${style.bg} ${style.border} text-muted-foreground/50 cursor-default`}
              data-testid={`order-${item.id}`}
            >
              <ShoppingBag className="w-3 h-3" />
              Coming Soon
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function TreasureChest() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStickers, setShowStickers] = useState(true);
  const [showTrophies, setShowTrophies] = useState(true);
  const [orderMsg, setOrderMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/profile/treasure-chest`);
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOrder = async (item) => {
    try {
      const res = await authFetch(`${API}/api/profile/treasure-chest/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ milestone_id: item.id }),
      });
      if (res.ok) {
        setOrderMsg({ type: 'ok', text: `${item.name} claimed! Fulfillment coming soon.` });
        load();
      } else {
        const err = await res.json();
        setOrderMsg({ type: 'err', text: err.detail || 'Failed to order' });
      }
    } catch {
      setOrderMsg({ type: 'err', text: 'Network error' });
    }
    setTimeout(() => setOrderMsg(null), 4000);
  };

  if (loading) return null;
  if (!data) return null;

  const { aura, stickers, trophies, unlocked_count, total_count, next_milestone, orders } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-testid="treasure-chest"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gem className="w-4 h-4 text-amber-400" />
          <p className="text-xs tracking-widest uppercase text-muted-foreground">Treasure Chest</p>
        </div>
        <span className="text-[10px] text-muted-foreground/40">{unlocked_count}/{total_count} unlocked</span>
      </div>

      {/* Next milestone progress */}
      {next_milestone && (
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3" data-testid="next-milestone">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] text-muted-foreground/50">Next unlock</p>
            <p className="text-[10px] font-bold text-foreground/80">{next_milestone.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, ((aura / next_milestone.threshold) * 100))}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="h-full bg-amber-400/60 rounded-full"
              />
            </div>
            <span className="text-[9px] text-amber-400/60 font-bold flex-shrink-0">{next_milestone.aura_needed} to go</span>
          </div>
        </div>
      )}

      {/* Order message */}
      <AnimatePresence>
        {orderMsg && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`text-[10px] p-2.5 rounded-xl text-center font-medium ${
              orderMsg.type === 'ok' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}
          >
            {orderMsg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stickers */}
      <div>
        <button
          onClick={() => setShowStickers(!showStickers)}
          className="flex items-center gap-2 mb-2 w-full"
          data-testid="stickers-toggle"
        >
          {showStickers ? <ChevronDown className="w-3 h-3 text-muted-foreground/30" /> : <ChevronRight className="w-3 h-3 text-muted-foreground/30" />}
          <Sticker className="w-3 h-3 text-blue-400/60" />
          <span className="text-[10px] font-bold text-blue-400/70 uppercase tracking-wider">Stickers</span>
          <span className="text-[8px] text-muted-foreground/25 ml-auto">{stickers.filter(s => s.unlocked).length}/{stickers.length}</span>
        </button>
        <AnimatePresence>
          {showStickers && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 gap-2">
                {stickers.map(s => (
                  <MilestoneCard key={s.id} item={s} order={orders?.[s.id]} onOrder={handleOrder} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Trophies */}
      <div>
        <button
          onClick={() => setShowTrophies(!showTrophies)}
          className="flex items-center gap-2 mb-2 w-full"
          data-testid="trophies-toggle"
        >
          {showTrophies ? <ChevronDown className="w-3 h-3 text-muted-foreground/30" /> : <ChevronRight className="w-3 h-3 text-muted-foreground/30" />}
          <Trophy className="w-3 h-3 text-amber-400/60" />
          <span className="text-[10px] font-bold text-amber-400/70 uppercase tracking-wider">Trophies</span>
          <span className="text-[8px] text-muted-foreground/25 ml-auto">{trophies.filter(t => t.unlocked).length}/{trophies.length}</span>
        </button>
        <AnimatePresence>
          {showTrophies && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 gap-2">
                {trophies.map(t => (
                  <MilestoneCard key={t.id} item={t} order={orders?.[t.id]} onOrder={handleOrder} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
